/**
 * Pure draft state, validation and wire mapping for the Change salary dialog
 * (`change_salary_dialog.tsx`). No React.
 *
 * Mirrors what `payroll.scheduleCompensationChange`
 * (`apps/api/src/trpc/routers/payroll.ts`) enforces so obviously bad input is
 * caught before a round trip: a positive amount for the chosen basis, an
 * effective date on/after the employment start and on/before the termination
 * date, and a note of at most 500 characters. The backend stays authoritative
 * (draft-pay-run overlap, same-date entries, mid-period currency changes are
 * only knowable server-side and come back as classified failures).
 *
 * Money is integer minor units throughout (docs/money-rules.md); the
 * `MoneyInput` in the dialog owns the decimal <-> cents conversion.
 */
import type { RealEmployeeRecord } from '../../data/wagepro_store_employees.js';
import type {
  CompensationPayBasis,
  CompensationReason,
  ScheduleCompensationInput,
} from '../../data/use_employee_compensation.js';

export const NOTE_MAX_LENGTH = 500;

/** The employee's pay as it stands today -- the subset of the employee record this dialog reads. */
export interface ChangeSalaryCurrent {
  pay_basis: CompensationPayBasis;
  monthly_salary_cents: number | null;
  hourly_rate_cents: number;
  /** `null` = the organisation base currency. */
  pay_currency_code: string | null;
  employment_start_date: string | null;
  termination_date: string | null;
}

export interface ChangeSalaryDraft {
  pay_basis: CompensationPayBasis;
  /** Minor units: monthly salary (salaried) or hourly rate (hourly). */
  amount_cents: number;
  /** ISO 4217 code, or `''` for the organisation base currency. */
  pay_currency_code: string;
  /** ISO `YYYY-MM-DD`, or `''` while unset. */
  effective_from: string;
  reason: CompensationReason;
  note: string;
  /** Set after a same-date conflict so the resubmission supersedes the existing entry. */
  replace_same_date: boolean;
}

export type ChangeSalaryDateError =
  | 'required'
  | 'before_start'
  | 'after_termination';
export type ChangeSalaryAmountError =
  | 'salary_required'
  | 'hourly_rate_required';

export interface ChangeSalaryErrors {
  effective_from?: ChangeSalaryDateError;
  amount?: ChangeSalaryAmountError;
  note?: 'too_long';
}

/** Narrows a real employee record to the pay facts this dialog reads. */
export function currentFromRecord(
  record: Pick<
    RealEmployeeRecord,
    | 'pay_basis'
    | 'monthly_salary_cents'
    | 'hourly_rate_cents'
    | 'pay_currency_code'
    | 'employment_start_date'
    | 'termination_date'
  >,
): ChangeSalaryCurrent {
  return {
    pay_basis: record.pay_basis,
    monthly_salary_cents: record.monthly_salary_cents,
    hourly_rate_cents: record.hourly_rate_cents,
    pay_currency_code: record.pay_currency_code,
    employment_start_date: record.employment_start_date,
    termination_date: record.termination_date,
  };
}

/** The amount currently in force for `basis` (0 when it was never configured). */
export function amountForBasis(
  current: ChangeSalaryCurrent,
  basis: CompensationPayBasis,
): number {
  return basis === 'salaried'
    ? (current.monthly_salary_cents ?? 0)
    : current.hourly_rate_cents;
}

/** The dialog's opening draft: today's pay, effective today (or the start date if that is still ahead). */
export function initialChangeSalaryDraft(
  current: ChangeSalaryCurrent,
  today: string,
): ChangeSalaryDraft {
  const start = current.employment_start_date ?? '';
  return {
    pay_basis: current.pay_basis,
    amount_cents: amountForBasis(current, current.pay_basis),
    pay_currency_code: current.pay_currency_code ?? '',
    effective_from: start !== '' && start > today ? start : today,
    reason: 'annual_review',
    note: '',
    replace_same_date: false,
  };
}

/** Whether the draft moves the pay currency away from the one in force. */
export function isCurrencyChanged(
  draft: ChangeSalaryDraft,
  current: ChangeSalaryCurrent,
): boolean {
  return draft.pay_currency_code !== (current.pay_currency_code ?? '');
}

export function validateChangeSalary(
  draft: ChangeSalaryDraft,
  current: ChangeSalaryCurrent,
): ChangeSalaryErrors {
  const errors: ChangeSalaryErrors = {};

  if (draft.effective_from === '') {
    errors.effective_from = 'required';
  } else if (
    current.employment_start_date !== null &&
    draft.effective_from < current.employment_start_date
  ) {
    errors.effective_from = 'before_start';
  } else if (
    current.termination_date !== null &&
    draft.effective_from > current.termination_date
  ) {
    errors.effective_from = 'after_termination';
  }

  if (draft.amount_cents <= 0) {
    errors.amount =
      draft.pay_basis === 'salaried'
        ? 'salary_required'
        : 'hourly_rate_required';
  }

  if (draft.note.length > NOTE_MAX_LENGTH) errors.note = 'too_long';

  return errors;
}

export function isChangeSalaryValid(
  draft: ChangeSalaryDraft,
  current: ChangeSalaryCurrent,
): boolean {
  return Object.keys(validateChangeSalary(draft, current)).length === 0;
}

/**
 * Builds the `scheduleCompensationChange` input. Only the amount that belongs
 * to the chosen basis is sent; the currency is `undefined` (keep the one in
 * force) unless the operator actually changed it, `null` for "organisation
 * base", or the ISO code. A blank note is omitted.
 */
export function buildScheduleInput(
  draft: ChangeSalaryDraft,
  current: ChangeSalaryCurrent,
): ScheduleCompensationInput {
  const note = draft.note.trim();
  let pay_currency_code: string | null | undefined;
  if (isCurrencyChanged(draft, current)) {
    pay_currency_code =
      draft.pay_currency_code === '' ? null : draft.pay_currency_code;
  }
  return {
    pay_basis: draft.pay_basis,
    ...(draft.pay_basis === 'salaried'
      ? { monthly_salary_cents: draft.amount_cents }
      : { hourly_rate_cents: draft.amount_cents }),
    ...(pay_currency_code !== undefined ? { pay_currency_code } : {}),
    effective_from: draft.effective_from,
    reason: draft.reason,
    ...(note !== '' ? { note } : {}),
    ...(draft.replace_same_date ? { replace_same_date: true } : {}),
  };
}
